import React, { FC } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  OnboardingDraft,
  OnboardingValidationContext,
  ValidationIssue,
  validateOnboardingDraft,
} from '@intelligent-play-v2/domain';

import { Button, ButtonType } from '~/components/button';
import { TextInput } from '~/components/forms';

import { FacilityImagePicker } from './FacilityImagePicker';
import { OnboardingPitchCard } from './OnboardingPitchCard';
import { SiblingCentre } from './PitchCentrePicker';
import {
  fieldStateFor,
  inputStateClass,
  issuesAt,
  messageAt,
} from './fieldState';

/** `pitches.0.sensors.1.id` → `Pitch 1 · sensor 2`, so a path reads as a place in the form. */
const describePath = (path: string): string =>
  path
    .replace(/^facility\b\.?/, 'Facility ')
    .replace(/pitches\.(\d+)\.?/, (_, index) => `Pitch ${Number(index) + 1} · `)
    .replace(
      /sensors\.(\d+)\.?/,
      (_, index) => `sensor ${Number(index) + 1} · `
    )
    .replace(
      /maintenanceSchedules\.(\d+)\.?/,
      (_, index) => `schedule ${Number(index) + 1} · `
    )
    .replace(/\.$/, '')
    .trim();

interface OnboardingReviewFormProps {
  draft: OnboardingDraft;
  /**
   * Issues that cannot be re-derived from the draft: problems with the paste itself, and
   * anything the server reported when a commit was refused.
   */
  standingIssues: ValidationIssue[];
  /** The ids and names already in the database, so collisions are caught as they are typed. */
  context: OnboardingValidationContext;
  /** JSON of each untouched default schedule, for labelling template-derived cards. */
  templateSignatures: string[];
  /** The overview image, held by the page; the pitch centres are marked on it here. */
  imageFile: File | null;
  imagePreviewUrl: string | null;
  onImageFileChange: (file: File | null) => void;
  onReviewed: (draft: OnboardingDraft) => void;
  onStartOver: () => void;
}

/**
 * Step 2: the editable draft.
 *
 * Every field is a live input — the whole point of the page is that a person can amend
 * anything the form got wrong before it reaches the database. Nothing here derives or maps
 * values; that all happens server-side so the parity fixtures cover it.
 *
 * The draft is re-validated on every keystroke with the same validator the commit
 * transaction runs, so a field says it is unhappy while the reviewer is looking at it and
 * goes quiet the moment it is fixed — rather than saving up its complaints for the commit
 * button.
 */
export const OnboardingReviewForm: FC<OnboardingReviewFormProps> = ({
  draft,
  standingIssues,
  context,
  templateSignatures,
  imageFile,
  imagePreviewUrl,
  onImageFileChange,
  onReviewed,
  onStartOver,
}) => {
  const methods = useForm<OnboardingDraft>({
    defaultValues: draft,
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<OnboardingDraft> = values => onReviewed(values);

  const { meta } = draft;
  // watching everything is the point: it is what makes validation live
  const values = methods.watch() as OnboardingDraft;
  const { errors, warnings } = validateOnboardingDraft(values, context);
  const issues = [...standingIssues, ...errors, ...warnings];
  /*
   * What stops the commit: the live result plus anything the server has said. Both are
   * listed rather than only painted on their inputs — a path with no visible field behind
   * it (a per-day operating window, a whole-draft complaint) would otherwise be invisible,
   * and a refused commit would read as a toast with no explanation.
   */
  const blocking = [
    ...standingIssues.filter(issue => issue.severity === 'error'),
    ...errors,
  ];
  const facilityId = values.facility?.id;

  /** Every pitch's marked centre, so each card can show the others for orientation. */
  const markedCentres = (values.pitches ?? []).map((pitch, index) => {
    const centre = pitch?.pitchCenterPixels;
    return centre && centre.x !== null && centre.y !== null
      ? {
          label: pitch.name?.trim() || `Pitch ${index + 1}`,
          x: centre.x,
          y: centre.y,
        }
      : null;
  });
  const siblingsOf = (pitchIndex: number): SiblingCentre[] =>
    markedCentres.filter(
      (centre, index): centre is SiblingCentre =>
        centre !== null && index !== pitchIndex
    );

  return (
    <FormProvider {...methods}>
      {/*
       * Deliberately not a <form>. Every button in the house component library is
       * type="submit", so implicit submission — pressing Enter in any text input — would
       * click the first one in the tree, which is a snapshot time's remove button. There is
       * nothing to submit natively: the continue button calls handleSubmit itself.
       */}
      <div>
        <div className="rounded border border-primary-250 bg-primary-100 p-5">
          <p className="text-lg font-semibold">Submission</p>
          <div className="mt-2 grid grid-cols-1 gap-x-8 gap-y-1 text-base lg:grid-cols-2">
            <p>
              <span className="text-primary-500">Submitted by </span>
              {meta.submitterEmail || '—'}
              <span className="text-primary-500"> on </span>
              {meta.submittedAt || '—'}
            </p>
            <p>
              <span className="text-primary-500">Times provided in </span>
              <strong>{meta.timesProvidedIn || 'not stated'}</strong>
            </p>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-base">
            {[
              ['Overview image', meta.overviewImageDriveLink],
              ['Labelled image', meta.labelledImageDriveLink],
              ['Maintenance schedule', meta.maintenanceScheduleDriveLink],
            ]
              .filter(([, link]) => !!link)
              .map(([label, link]) => (
                <a
                  key={label}
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-green hover:text-green-dark"
                >
                  {label} ↗
                </a>
              ))}
          </div>
          <p className="mt-2 text-sm text-primary-500">
            You will need these to fill in the pitch centre pixels and the
            maintenance schedules.
          </p>
        </div>

        <div
          className={`mt-4 rounded p-4 text-base ${
            blocking.length > 0
              ? 'border border-red bg-red-lightest text-red-dark'
              : 'border border-green bg-green-lightest text-green-dark'
          }`}
        >
          <p className="font-semibold">
            {blocking.length > 0
              ? `${blocking.length} thing${
                  blocking.length === 1 ? '' : 's'
                } still to fix before this can be saved:`
              : 'Every required field is filled in.'}
            {warnings.length > 0 && (
              <span className="ml-2 font-normal text-primary-600">
                {`${warnings.length} warning${
                  warnings.length === 1 ? '' : 's'
                } to read before committing.`}
              </span>
            )}
          </p>
          {blocking.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {blocking.map((issue, index) => (
                <li key={`${issue.path}-${index}`}>
                  {/* the separator is a text node, so a copied list reads as well as it looks */}
                  {!!issue.path && <code>{describePath(issue.path)}</code>}
                  {issue.path ? ' — ' : ''}
                  {issue.message}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-8">
          <p className="text-xl font-semibold">Facility</p>
          <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <TextInput
              label="Facility ID"
              isLightMode
              required
              register={methods.register('facility.id', {
                valueAsNumber: true,
              })}
              className={inputStateClass(fieldStateFor(issues, 'facility.id'))}
              errorMessage={messageAt(issues, 'facility.id')}
            />
            <TextInput
              label="Name"
              isLightMode
              required
              register={methods.register('facility.name')}
              className={inputStateClass(
                fieldStateFor(issues, 'facility.name')
              )}
              errorMessage={messageAt(issues, 'facility.name')}
            />
            <TextInput
              label="PitchFinder facility ID"
              isLightMode
              type="number"
              register={methods.register('facility.pitchFinderId', {
                setValueAs: value => (value === '' ? null : Number(value)),
              })}
            />
            <TextInput
              label="Address"
              isLightMode
              register={methods.register('facility.address')}
            />
            <TextInput
              label="Postcode"
              isLightMode
              register={methods.register('facility.postcode')}
            />
          </div>
          {issuesAt(warnings, 'facility.name').map(issue => (
            <p key={issue.message} className="mt-2 text-sm text-yellow">
              {issue.message}
            </p>
          ))}
          <div className="mt-4">
            <FacilityImagePicker
              facilityId={facilityId}
              imageFile={imageFile}
              imagePreviewUrl={imagePreviewUrl}
              onImageFileChange={onImageFileChange}
            />
          </div>
        </div>

        {draft.pitches.map((_, pitchIndex) => (
          <OnboardingPitchCard
            // pitches come from the paste and are never reordered, so the index is stable
            key={`pitch-${pitchIndex}`}
            pitchIndex={pitchIndex}
            issues={issues}
            templateSignatures={templateSignatures}
            imageUrl={imagePreviewUrl}
            siblingCentres={siblingsOf(pitchIndex)}
          />
        ))}

        <div className="mt-8 flex justify-between">
          <Button
            text="Start over"
            type={ButtonType.Outline}
            onClick={onStartOver}
          />
          <Button
            text="Continue to preview"
            type={ButtonType.Primary}
            onClick={methods.handleSubmit(onSubmit)}
          />
        </div>
      </div>
    </FormProvider>
  );
};
